import { z } from "zod";
import { VOLUNTEER_CATEGORIES, ASSIGNMENT_STATUSES } from "../types/volunteer-coordination.js";

const categorySchema = z.enum(VOLUNTEER_CATEGORIES);

// HH:mm regex -- same shape used by 083 EventSeriesSlot.
const hhmmSchema = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Time must be in HH:mm format");

const emptyStringToNull = z.preprocess(
  (v) => (typeof v === "string" && v.trim() === "" ? null : v),
  z.string().max(5000).nullish(),
);

// --- EventTask (organiser-side) ---

export const createEventTaskSchema = z.object({
  title: z.string().min(1, "Title is required").max(200),
  description: emptyStringToNull,
  category: categorySchema.default("general"),
  needed_count: z.number().int().min(1).max(999).default(1),
  starts_at_local: hhmmSchema.nullish(),
  ends_at_local: hhmmSchema.nullish(),
  contact_user_id: z.string().min(1).nullish(),
}).refine(
  (v) => !v.starts_at_local || !v.ends_at_local || v.starts_at_local <= v.ends_at_local,
  { message: "Task end time must be at or after the start time", path: ["ends_at_local"] },
);
export type CreateEventTaskInput = z.infer<typeof createEventTaskSchema>;

export const updateEventTaskSchema = z.object({
  title: z.string().min(1).max(200).optional(),
  description: emptyStringToNull,
  category: categorySchema.optional(),
  needed_count: z.number().int().min(1).max(999).optional(),
  starts_at_local: hhmmSchema.nullish(),
  ends_at_local: hhmmSchema.nullish(),
  contact_user_id: z.string().nullish(),
});
export type UpdateEventTaskInput = z.infer<typeof updateEventTaskSchema>;

// --- Assignments ---

// Parent claim payload.
export const claimTaskSchema = z.object({
  user_notes: z.preprocess(
    (v) => (typeof v === "string" && v.trim() === "" ? null : v),
    z.string().max(1000).nullish(),
  ),
});
export type ClaimTaskInput = z.infer<typeof claimTaskSchema>;

// Organiser-driven status change. Reason required for destructive transitions
// (cancelled / no_show) so there's always context on why a slot fell through.
const organiserTargetStatusSchema = z.enum(["accepted", "completed", "no_show", "cancelled"]);

export const updateAssignmentStatusSchema = z.object({
  to_status: organiserTargetStatusSchema,
  organiser_notes: z.preprocess(
    (v) => (typeof v === "string" && v.trim() === "" ? null : v),
    z.string().max(2000).nullish(),
  ),
}).refine(
  (v) => {
    if (v.to_status === "cancelled" || v.to_status === "no_show") {
      return typeof v.organiser_notes === "string" && v.organiser_notes.trim().length > 0;
    }
    return true;
  },
  { message: "A note is required when cancelling or marking no-show", path: ["organiser_notes"] },
);
export type UpdateAssignmentStatusInput = z.infer<typeof updateAssignmentStatusSchema>;

// --- VolunteerOffer (parent-side CRUD) ---

export const createVolunteerOfferSchema = z.object({
  school_id: z.string().min(1, "School is required"),
  category: categorySchema,
  title: z.string().min(1, "Title is required").max(100),
  description: z.preprocess(
    (v) => (typeof v === "string" && v.trim() === "" ? null : v),
    z.string().max(2000).nullish(),
  ),
  active: z.boolean().optional(),
});
export type CreateVolunteerOfferInput = z.infer<typeof createVolunteerOfferSchema>;

export const updateVolunteerOfferSchema = z.object({
  category: categorySchema.optional(),
  title: z.string().min(1).max(100).optional(),
  description: z.preprocess(
    (v) => (typeof v === "string" && v.trim() === "" ? null : v),
    z.string().max(2000).nullish(),
  ),
  active: z.boolean().optional(),
});
export type UpdateVolunteerOfferInput = z.infer<typeof updateVolunteerOfferSchema>;
